/**
 * Placeholder garden art: simple sprites drawn in code, painted over each
 * biome's map (the shared placeholder map until the Lab's maps arrive). It exists so the garden works before the Thicket
 * RPG Lab's art is brought in; replace it, don't build on it.
 */
import type { Biome } from '@thicket/core';
import { COLS, H, PLACEHOLDER_MAP, ROWS, TILE, W } from '../../maps.js';
import type { GardenArt } from '../types.js';
import {
  ANIMAL_SPRITES, drawBedPlant, drawBloom, drawBud, drawTree, drawWeed, FLOWERS, hash, spr, treeTop,
} from './sprites.js';

interface Look { grass: [string, string, string]; forest: [string, string, string]; path: string; water: [string, string] }

const LOOKS: Record<Biome, Look> = {
  meadow: { grass: ['#6fae4a', '#5f9c40', '#82bf58'], forest: ['#2f6a35', '#3e7a46', '#245a2c'], path: '#c9ab7a', water: ['#4f9fc4', '#7cc0dc'] },
  wetland: { grass: ['#6e8f4a', '#5a7a3c', '#87a35c'], forest: ['#3d5c3a', '#4d6e45', '#2f4a2e'], path: '#9c8a64', water: ['#4d6f5e', '#6f8f7c'] },
  jungleRuins: { grass: ['#4f9a3e', '#3f8433', '#66b04f'], forest: ['#2f6a35', '#3f7f40', '#1f4f26'], path: '#a8a08a', water: ['#3f8f88', '#62aaa2'] },
  canyon: { grass: ['#c98f5a', '#b57a48', '#dba574'], forest: ['#9a5a36', '#ae6b42', '#7f4528'], path: '#e0c08e', water: ['#5f9fb0', '#86bccb'] },
  pineRidge: { grass: ['#5f9a58', '#4e8549', '#76ad6d'], forest: ['#2c5a40', '#3a6c4e', '#1f4430'], path: '#b8a27a', water: ['#4a86a8', '#72a6c4'] },
  woodlandLake: { grass: ['#679f4c', '#568a40', '#7cb35e'], forest: ['#355f3c', '#44714a', '#28492e'], path: '#bda57a', water: ['#3f86b0', '#68a6cc'] },
  coast: { grass: ['#d8c58e', '#c7b277', '#e8d8a6'], forest: ['#7a9a6a', '#8aaa78', '#5f7f54'], path: '#efe2bd', water: ['#3f9ec4', '#6cbcdc'] },
  mountainSummit: { grass: ['#8aa37a', '#76906a', '#a0b892'], forest: ['#55705a', '#647f68', '#425a48'], path: '#c2b8a4', water: ['#6aa6c4', '#94c2da'] },
  sacredGrove: { grass: ['#4f8f6a', '#3f7a5a', '#64a57e'], forest: ['#2f4f5a', '#3c5f6a', '#223c45'], path: '#a8b0a0', water: ['#5a8fb0', '#84aecb'] },
};

const backgrounds = new Map<Biome, HTMLCanvasElement>();

function paint(biome: Biome): HTMLCanvasElement {
  const look = LOOKS[biome];
  const bg = document.createElement('canvas');
  bg.width = W;
  bg.height = H;
  const c = bg.getContext('2d')!;
  for (let r = 0; r < ROWS; r++) {
    for (let col = 0; col < COLS; col++) {
      const ch = PLACEHOLDER_MAP.rows[r]![col]!;
      const x = col * TILE;
      const y = r * TILE;
      if (ch === 'w') {
        c.fillStyle = look.water[0];
        c.fillRect(x, y, TILE, TILE);
        continue;
      }
      c.fillStyle = look.grass[0];
      c.fillRect(x, y, TILE, TILE);
      for (let i = 0; i < 10; i++) {
        const h = hash(biome, col, r, i);
        c.fillStyle = h % 2 ? look.grass[1] : look.grass[2];
        c.fillRect(x + (h % TILE), y + ((h >>> 8) % TILE), 1, 1);
      }
      if (ch === 'p') {
        c.fillStyle = look.path;
        c.fillRect(x + 3, y, 10, TILE);
      } else if (ch === 'b') {
        c.fillStyle = '#2b1d14';
        c.fillRect(x, y, TILE, TILE);
        c.fillStyle = '#9a9488';
        c.fillRect(x + 1, y + 1, TILE - 2, TILE - 2);
        c.fillStyle = '#5c3d26';
        c.fillRect(x + 3, y + 3, TILE - 6, TILE - 6);
      }
    }
  }
  // Forest walls as overlapping round crowns, drawn after the ground so they spill over it.
  for (let r = 0; r < ROWS; r++) {
    for (let col = 0; col < COLS; col++) {
      if (PLACEHOLDER_MAP.rows[r]![col] !== 'F') continue;
      const h = hash('F', col, r, biome);
      const cx = col * TILE + 8 + ((h % 5) - 2);
      const cy = r * TILE + 8 + (((h >>> 4) % 5) - 2);
      const rad = 9 + (h % 3);
      for (let dy = -rad; dy <= rad; dy++) {
        for (let dx = -rad; dx <= rad; dx++) {
          if (dx * dx + dy * dy > rad * rad) continue;
          c.fillStyle = dx + dy > rad * 0.5 ? look.forest[2] : dx + dy < -rad * 0.6 ? look.forest[1] : look.forest[0];
          c.fillRect(cx + dx, cy + dy, 1, 1);
        }
      }
    }
  }
  return bg;
}

export const placeholderArt: GardenArt = {
  name: 'placeholder',
  map: () => PLACEHOLDER_MAP,
  background(biome) {
    let bg = backgrounds.get(biome);
    if (!bg) backgrounds.set(biome, (bg = paint(biome)));
    return bg;
  },
  ambient(ctx, biome, { tick, reduce }) {
    if (reduce) return;
    ctx.fillStyle = LOOKS[biome].water[1];
    for (let r = 0; r < ROWS; r++) {
      for (let col = 0; col < COLS; col++) {
        if (PLACEHOLDER_MAP.rows[r]![col] !== 'w') continue;
        const phase = (tick + hash(col, r)) % 30;
        if (phase < 12) ctx.fillRect(col * TILE + ((hash(r, col) + phase) % 12) + 2, r * TILE + (hash(col, r) % 12) + 2, 3, 1);
      }
    }
  },
  tree: (ctx, t) => drawTree(ctx, t.species, t.stage, t.sparse, t.x, t.y, t.seed, t.tick),
  treePerch: (stage, at) => treeTop(stage, at.x, at.y),
  bud: (ctx, a) => drawBud(ctx, a.x, a.y, a.pace, a.drooping),
  bloom: (ctx, a) => drawBloom(ctx, a.x, a.y, a.pace, a.daysLeft, a.tick),
  flower(ctx, a) {
    const f = FLOWERS[a.seed];
    if (f) spr(ctx, f, a.x - 3, a.y - f.h + 1);
  },
  bedPlant: (ctx, a) => drawBedPlant(ctx, a.stage, a.x, a.y, a.seed),
  weed: (ctx, a) => drawWeed(ctx, a.x, a.y),
  animal(ctx, a) {
    const s = ANIMAL_SPRITES[a.kind];
    if (!s) return;
    const hop = a.kind === 'toad' && a.moving && Math.floor(a.tick / 6) % 2 ? 1 : 0;
    spr(ctx, s, a.x - s.w / 2, a.y - s.h - hop, a.flip);
  },
  animalSize: (kind) => ({ w: ANIMAL_SPRITES[kind]?.w ?? 8, h: ANIMAL_SPRITES[kind]?.h ?? 8 }),
};
